import { JSX, useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { logError } from '@dx-ui/framework-logger';
import type { Config } from 'isomorphic-dompurify';

export type SanitizedHtmlProps<T extends keyof JSX.IntrinsicElements = 'div'> = {
  /** The HTML string to sanitize and render */
  html: string;
  /** Optional DOMPurify configuration for sanitization */
  sanitizeOptions?: Config;
  /** Optional HTML element to render as wrapper - default is <div> */
  as?: T;
  /** Optional custom sanitization function that overrides DOMPurify */
  customSanitizeFn?: (input: string) => string;
} & Omit<JSX.IntrinsicElements[T], 'dangerouslySetInnerHTML'>;

/**
 * A secure component for rendering sanitized HTML content.
 * This component should ALWAYS be used instead of `dangerouslySetInnerHTML`
 */
export const SanitizedHtml = <T extends keyof JSX.IntrinsicElements>({
  html,
  sanitizeOptions,
  as = 'div' as T,
  customSanitizeFn,
  ...restProps
}: SanitizedHtmlProps<T>) => {
  const sanitized = useMemo(() => {
    try {
      if (!html || typeof html !== 'string') {
        return '';
      }
      if (customSanitizeFn) {
        return customSanitizeFn(html);
      }

      if (DOMPurify?.sanitize) {
        return DOMPurify.sanitize(html, sanitizeOptions);
      }
      return '';
    } catch (error) {
      logError('Failed to process HTML content in SanitizedHtml component', { error });
      return '';
    }
  }, [customSanitizeFn, html, sanitizeOptions]);

  const Component = as as React.ElementType;

  return (
    <Component
      data-testid="sanitized-html"
      dangerouslySetInnerHTML={{ __html: sanitized }}
      {...restProps}
    />
  );
};

export default SanitizedHtml;
